import moment from 'moment';
import office from 'src/services/office';

import { executeFlowstep } from 'src/api';
import { ws } from 'src/api/ws';

import { Flowstep } from 'src/services/runtime/flowstep';
import { substituteQuery, substituteFilters } from 'src/services/substitution';
import { clearAllFilters, applyFilters } from 'src/services/runtime/dataTable';

import { Type, PullMode, TruncateDataError } from 'shared/utils/flowstep';
import { SalesforceClientReportProvider } from 'shared/providers/salesforce/testing/salesforce-client-report-provider';
import * as QA from 'shared/utils/queryAnalysis';
import { getNewSheetModeWorksheetName, NEW_SHEET_MODE_DATE_TIME_FORMAT } from 'shared/excel/excel';
import { metadataHandler } from 'src/services/metadataHandler';
import { pick } from 'shared/utils/utils';
import { createDataChopper } from 'src/services/dataChopper';
import { PULL_FLOWSTEP_ADDITIONAL_HEADERS } from 'shared/excel/runtime/flowstepExecution';

export class PullFlowstep extends Flowstep {
  constructor(flowstep, meta, parent) {
    super(flowstep, meta, parent);

    this.insertRowOffset = 0;
  }

  async _runFlowstep_() {
    let timestamp;
    try {
      const { officeStore, cancelToken, flowstep } = this;

      const { target = 'A1', worksheetId, type, options } = flowstep;
      const { pullMode, createDateTimeColumn } = options;
      if (PullMode.NewSheet === pullMode && this.dataTable.realDataRowCount) {
        await this.createNewWorksheet();
      }

      const { dataTable } = this;
      const { worksheet } = dataTable;
      const { context } = worksheet;

      metadataHandler.context = context;

      // const table = this.dataTable.realTable;

      this.fireEvent('precollecting');
      const filters = await clearAllFilters(worksheet);
      console.log('PullFlowstep::runFlowstep: Cleared filters, saved filters: ', filters.length);

      const request = { flowstep: { type } };

      if (type === Type.Soql) {
        this.fireEvent('preprocessing');
        const query = await substituteQuery(context, options.query, type, worksheetId, this.limits); // Use flowstep's worksheet id even if we create new worksheet
        Object.assign(request.flowstep, { query, options: pick(options, ['queryAll', 'showLabels']) });
      }
      else if (type === Type.Report) {
        const requestOptions = pick(options, ['excludedFields', 'filters']);
        requestOptions.filters = await substituteFilters(context, requestOptions.filters, worksheetId, this.limits);
        Object.assign(request.flowstep, { reportId: options.report_id, options: requestOptions });
      }
      else if (type === Type.Csv) {
        Object.assign(request.flowstep, { url: options.url });
      }
      else if (type === Type.Sosl) {
        const query = await substituteQuery(context, options.query, type, worksheetId, this.limits); // Use flowstep's worksheet id even if we create new worksheet
        Object.assign(request.flowstep, { query, options: pick(options, ['showLabels']) });
      }
      else if (type === Type.DebugReport) {
        Object.assign(request.flowstep, { reportId: options.report_id, options: pick(options, ['excludedFields']) });
      }

      console.log('PullFlowstep::runFlowstep: Request ', request);
      const flowstepColumns = await officeStore.getFlowstepColumns(flowstep);
      const { timestamp: runTimestamp, isTruncated } = await this.updateFlowstepData(request, flowstepColumns);
      timestamp = runTimestamp;

      this.fireEvent('clearing');

      if (pullMode !== PullMode.Overwrite || this.processedRowCount) {
        this.dataTable.fitRowsToSize(this.insertRowOffset + this.processedRowCount);
      }
      else {
        this.dataTable.clear();
      }

      // await office.clearUnusedRange(worksheet, table, target, columnCount, rowCount);

      if (options.useDropdownsForPicklists ?? false) {
        this.fireEvent('applying-validation')
        // await this.applyDataValidation(rowCount - 1, columnCount); // Exclude headers
        await this.applyDataValidation(flowstepColumns);
      }

      // this._fireEvent('postprocessing');

      this.fireEvent('restoring-filters');
      await applyFilters(filters);

      // if (!table && flowstep.options.createDataTable) {
      //   this.fireEvent('creating-table');
      //   await office.createDataTable(worksheet, target, columnCount, rowCount);
      // }

      // this.resultSuccessRange = worksheet.getRange(createRange(target, columnCount, rowCount));

      if (!this.dataTable.isRealTable && flowstep.options.createDataTable) {
        this.fireEvent('creating-table');
        try {
          await this.dataTable.convertToRealTable();
        }
        catch (err) {
          console.log('PullFlowstep::runFlowstep: Unable to create data table:', err)
          if (err.message.includes('already exists')) {
            await this.dataTable.convertToRealTable();
          }
          else {
            throw err;
          }
        }

      }

      if (pullMode === PullMode.Append && createDateTimeColumn) {
        this.fireEvent('inserting-dateTime-column')
        await this.updateDateTimeColumn(timestamp);
      }


      this.resultSuccessRange = this.dataTable.range;
      const warnings = [];
      if (isTruncated) {
        warnings.push(new TruncateDataError());
      }
      return { timestamp, warnings };
    }
    catch (err) {
      console.log('PullFlowstep::runFlowstep: Error occurred:', err);
      throw err;
    }
    finally {
      await this.setTimestampCellValue(timestamp);
    }
  }

  async createNewWorksheet() {
    const worksheetPattern = getNewSheetModeWorksheetName(this.flowstep.name, moment().format(NEW_SHEET_MODE_DATE_TIME_FORMAT));

    this.fireEvent('creating-new-worksheet', worksheetPattern);
    this.worksheet = await office.createWorksheetWithCtx(this.context, worksheetPattern, false);

    this.logger.setLogParams({ worksheetName: this.worksheet.name });
    await this.detectTargetTable();
  }

  async updateFlowstepData(request, flowstepColumns) {
    const { dataTable, flowstep, cancelToken, userStore } = this;
    console.log(userStore.settings.disableWebsocket, 'PullFlowstep::updateFlowstepData: disableWebsocket setting');

    const skipHeadersPatching = PullMode.Append === flowstep.options.pullMode && !this.dataTable.isEmpty;
    this.insertRowOffset = skipHeadersPatching ? dataTable.realDataRowCount : 0;
    let headers = null;

    console.log('PullFlowstep::updateFlowstepData: Starting data update');
    this.fireEvent('remote-receiving');
    let timestamp = '1900-02-01 00:11:11';
    let isTruncated = false;

    const { push, finalizer } = createDataChopper(async (values) => {
      console.log(`PullFlowstep::updateFlowstepData: Received chunk with ${values.length} rows`);
      const { dataTable } = this;

      if (!headers) {
        headers = values.splice(0, 1)[0];
        const columnCount = headers.length;
        const columnCapacity = dataTable.columnCount - this.targetColumnOffset;
        if (!skipHeadersPatching) {
          console.log(`PullFlowstep::updateFlowstepData: Patching headers`);
          const columnsToInsert = columnCount - columnCapacity;
          if (columnsToInsert > 0) {
            dataTable.addColumns(columnsToInsert);
          }
          dataTable.setHeaders(headers, this.targetColumnOffset);
        }
        else if (columnCapacity < columnCount) {
          throw new Error(`Column count in 'Append' mode mismatch. Expected: <= ${columnCapacity}, observed: ${columnCount}`);
        }
      }

      console.log(`PullFlowstep::updateFlowstepData: Pushing chunk with ${values.length} rows`);
      this.fireEvent('processing-chunk');
      cancelToken.throwIfRequested();

      // values array might be spliced in dataTable.setValues, so save the original size here
      const valuesCount = values.length;
      if (valuesCount) {
        const position = this.insertRowOffset + this.processedRowCount;
        console.log(`PullFlowstep::updateFlowstepData: Setting values at position ${position}`);
        await dataTable.safeSetValues(position, values, this.targetColumnOffset);
      }

      this.processedRowCount += valuesCount;
      console.log(`PullFlowstep::updateFlowstepData: Processed row count ${this.processedRowCount}`);
      await this.context.sync();

      console.log(`PullFlowstep::updateFlowstepData: Finished processing chunk`);
    });


    if (userStore.settings.disableWebsocket) {
      timestamp = await executeFlowstep(request, this.userStoreToken, push);
    }
    else {
      console.log('PullFlowstep::updateFlowstepData: Starting websocket execution');
      await ws.action('runflowstep', request, {
        cancelToken,
        callback: (rows) => {
          console.log('PullFlowstep::updateFlowstepData: Received websocket message');
          if (!Array.isArray(rows)) {
            console.log('PullFlowstep::updateFlowstepData: Received timestamp message', rows.timestamp);
            timestamp = rows.timestamp;
            return;
          }

          const columnsMap = new Map(flowstepColumns.map(column => [column.columnIndex, column]));

          const preparedRows = rows.map(row =>
            row.map((value, index) => {
              if (value.length > 32767) {
                isTruncated = true;
                return value.substring(0, 32767);
              }

              if (columnsMap.has(index)) {
                const { rawField } = columnsMap.get(index);
                if (rawField && QA.isSinglePicklistField(rawField)) {
                  return value?.length > 0 ? `'${value}` : value;
                }
              }

              return value;
            })
          )
          console.log(`PullFlowstep::updateFlowstepData: Pushing chunk with ${preparedRows.length} rows (truncated: ${isTruncated})`);
          push(preparedRows)
        }
      });
    }

    await finalizer();
    return { timestamp, isTruncated };
  }

  async updateDateTimeColumn(timestamp) {
    const DateTimeColumnName = PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime;

    const lastColumnName = (await this.dataTable.getHeaders())[this.dataTable.columnCount - 1];

    if (lastColumnName !== DateTimeColumnName) {
      this.dataTable.addColumns(1, [DateTimeColumnName])
    }

    let insertTimestampOffset = this.insertRowOffset;
    const columnOffset = this.dataTable.columnCount - 1;

    const { push, finalizer } = createDataChopper(async (values) => {
      this.dataTable.setValues(insertTimestampOffset, values, columnOffset);
      insertTimestampOffset += values.length;
    });

    push(new Array(this.processedRowCount).fill([timestamp]))
    await finalizer();
  }

  // async applyDataValidation(dataRowCount, columnCount) {
  async applyDataValidation(flowstepColumns) {
    const { flowstep, officeStore } = this;
    const picklists = flowstepColumns.reduce((out, x) => {
      const { rawField } = x;
      if (QA.isSinglePicklistField(rawField)) {
        out.push({ ...x, isRestricted: QA.isRestrictedPicklistField(rawField) });
      }
      return out;
    }, []);

    // console.log('PullFlowstep::applyDataValidation: ', picklists);

    const { realDataRowCount } = this.dataTable;
    if (!picklists.length || !realDataRowCount) { return; }
    // if (!picklists.length || !dataRowCount) { return; }

    const { showLabels } = flowstep.options;
    const { userName } = this.userStore;

    for (const pl of picklists) {
      const { objectName, rawField } = pl;
      const { valuesRange, labelsRange } = await metadataHandler.getPicklistRanges(userName, objectName, rawField);
      pl.picklistValuesAddress = showLabels ? labelsRange : valuesRange;
    }

    await this.createDataValidationRules(picklists);
    // await this.createDataValidationRules(picklists, dataRowCount, columnCount);
  }

  // async createDataValidationRules(picklists, dataRowCount, columnCount) {
  async createDataValidationRules(picklists) {
    const { userName } = this.userStore;
    const { context, dataTable, flowstep } = this;
    // const { target } = flowstep;

    // const { rowIndex: startRowIndex, columnIndex: startColumnIndex } = rangeAddressToIndexes(target);
    // TODO: do we need to clear other validation rules as well?
    // worksheet.getRangeByIndexes(startRowIndex + 1, startColumnIndex, dataRowCount, columnCount).dataValidation.clear();

    const rangesToClear = [];

    for (let columnIndex = 0; columnIndex < dataTable.columnCount; ++columnIndex) {
      // const range = worksheet.getRangeByIndexes(
      //   startRowIndex + 1, startColumnIndex + columnIndex,
      //   dataRowCount, 1);
      const range = dataTable.getColumns(columnIndex, 1);

      const picklist = picklists.find(x => x.columnIndex + this.targetColumnOffset === columnIndex);
      if (!picklist) {
        rangesToClear.push(range.dataValidation.load(['rule']));
        continue;
      }

      const { picklistValuesAddress: address, isRestricted, rawField: { name }, objectName } = picklist;
      const source = context.workbook.worksheets.getItem(address.sheet).getRange(address.shortAddress);

      range.dataValidation.clear();
      range.dataValidation.rule = { list: { inCellDropDown: true, source } };
      range.dataValidation.prompt = {
        showPrompt: true,
        title: `${isRestricted ? 'Restricted picklist' : 'Picklist'} '${name}'`,
        message: `Object name: '${objectName}'`
      }
      range.dataValidation.errorAlert = {
        message: `Pickilst '${name}' of object '${objectName}' ${isRestricted ? 'is restricted' : `doesn't have such a value`}`,
        showAlert: true,
        title: 'Invalid pickilst value',
        style: isRestricted ? Excel.DataValidationAlertStyle.stop : Excel.DataValidationAlertStyle.warning
      };
    }

    await context.sync();

    if (!rangesToClear.length) {
      return;
    }

    const { userData } = await metadataHandler.getUserData(userName);
    const worksheetTemplateName = `=${userData.picklists.worksheetName}!`;

    rangesToClear.forEach(dataValidation => {
      const source = dataValidation.rule.list?.source;
      const toRemove = source && typeof source === 'string' && source.startsWith(worksheetTemplateName);
      if (toRemove) {
        dataValidation.clear();
      }
    });
  }

};
